import React, { useEffect, useRef, useState } from "react";
import { Container, Button, Modal, Card } from "react-bootstrap";
import QuizStart from "../course-resume/Quiz/QuizStart";
import QuizResult from "../course-resume/Quiz/QuizResult";
import { Spinner, Alert } from "react-bootstrap";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import { Info, AlertTriangle } from "react-feather";
import Questions from "../course-resume/Quiz/Questions";

dayjs.extend(isBetween);
import { evaluateAnswer, sendEvaluateAnswerBeacon } from "ServerAPI/serverAPI";
import { useCookies } from "react-cookie";
import { useLocation, useParams } from "react-router-dom";
import { useToast } from "context/toast-context/ToastProvider";
import NavigationButton from "./NavgiationButtons";
import ReadyForQuiz from "./ReadyForQuiz";

// Max-Attempts quizzes (isMaxAttemptsQuiz: true) don't reliably populate the top-level
// `quizResults` field with real data — it's either absent entirely, or a "shell" object like
// `{ results: [] }` with none of score/correctQuestions/totalQuestions/passed (seen once a
// teacher-review/redo cycle has happened). `Object.keys().length > 0` treats that shell as
// populated since the `results` key exists, even though it's empty — check for an actual
// non-empty `results` array instead, so we correctly fall back to `maxAttemptsHistory`.
export const getLatestQuizResults = (content) => {
  if (Array.isArray(content?.quizResults?.results) && content.quizResults.results.length > 0) {
    return content.quizResults;
  }
  if (Array.isArray(content?.maxAttemptsHistory) && content.maxAttemptsHistory.length > 0) {
    return content.maxAttemptsHistory[content.maxAttemptsHistory.length - 1]?.quizResults || null;
  }
  return null;
};

const QuizViewer = ({
  activeContent,
  courseDetails,
  setActiveContent,
  quizStatus,
  setQuizStatus,
  quizStartedAt,
  setQuizStartedAt,
  isMaxAttemptsQuiz,
  attemptNumber,
  maxAttemptsLimit,
  remainingAttempts,
  onAttemptSubmitted,
}) => {
  const { showToast } = useToast();
  // Guards against double-counting an attempt when an auto-submit (refresh/offline)
  // has already optimistically recorded it before the real evaluateAnswer response lands.
  const autoSubmitCountedRef = useRef(false);
  // All hooks MUST be declared before any early returns (Rules of Hooks)
  const [loading, setLoading] = useState(false);
  const [cookies, setCookies] = useCookies();
  const [score, setScore] = useState("");
  const [userScore, setUserScore] = useState("");
  const [totalScore, setTotalScore] = useState("");
  const [percentageScore, setPercentageScore] = useState("");
  const [correctQuestionsCount, setCorrectQuestionsCount] = useState("");
  const [totalQuestionsCount, setTotalQuestionsCount] = useState("");
  const [status, setStatus] = useState("");
  const [reviewCorrect, setReviewCorrect] = useState([]);
  const { courseId, sectionId } = useParams();
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [proctoringLogs, setProctoringLogs] = useState(null);
  const [questionTimeTracking, setQuestionTimeTracking] = useState({});
  const [passPercentage, setPassPercentage] = useState("");
  const location = useLocation();
  const [quizTime, setQuizTime] = useState(5);
  
  // Scheduling States
  const [isLocked, setIsLocked] = useState(false);
  const [lockReason, setLockReason] = useState("");
  const [showLateModal, setShowLateModal] = useState(false);
  const [effectiveTimeSet, setEffectiveTimeSet] = useState(false);
  const [failedRedoReviewMode, setFailedRedoReviewMode] = useState(false);

  useEffect(() => {
    if (activeContent?.contentType !== 'quiz' && activeContent?.contentType !== 'quizSubjective') {
      return;
    }

    const checkScheduling = () => {
      if (!activeContent?.scheduledAttempt) {
        setQuizTime(activeContent?.quizTimer || 5);
        setIsLocked(false);
        return;
      }

      const now = dayjs();
      const startStr = `${activeContent.startDate} ${activeContent.startTime}`;
      const endStr = `${activeContent.endDate} ${activeContent.endTime}`;
      const startTimeObj = dayjs(startStr);
      const endTimeObj = dayjs(endStr);

      // 1. Check if too early
      if (now.isBefore(startTimeObj)) {
        setIsLocked(true);
        setLockReason(`This quiz will be available on ${startTimeObj.format("MMM D, YYYY")} at ${startTimeObj.format("h:mm A")}`);
        return;
      }

      // 2. Check if window closed
      if (now.isAfter(endTimeObj)) {
        setIsLocked(true);
        setLockReason("The quiz window has closed.");
        return;
      }

      // 3. Late Start Check — only show modal ONCE
      const windowRemainingMinutes = endTimeObj.diff(now, "minute");
      const standardTimer = activeContent.quizTimer || 5;

      if (windowRemainingMinutes < standardTimer) {
        setQuizTime(Math.max(1, windowRemainingMinutes));
        if (!effectiveTimeSet) {
          setShowLateModal(true);
          setEffectiveTimeSet(true);
        }
      } else {
        setQuizTime(standardTimer);
      }
      
      setIsLocked(false);
    };

    checkScheduling();
  }, [activeContent]);

  const getFinish = async () => {
    // Basic safety checks (production-style)
    if (activeContent?.contentType !== 'quiz' && activeContent?.contentType !== 'quizSubjective') {
      return;
    }

    if (activeContent?.completed) {
      return;
    }

    if (!selectedAnswers || Object.keys(selectedAnswers).length === 0) {
      return;
    }

    setLoading(true);
    const questionID_userAns = (activeContent?.questions || []).map((question) => {
      const answer = selectedAnswers[question.id] || "";
      return {
        questionId: String(question.id),
        userAnswer: String(answer),
        answerSelectionType: question?.answerSelectionType || "single",
        point: question?.point || 1
      };
    }).sort((a, b) => Number(a.questionId) - Number(b.questionId));

    const payload = {
      questionID_userAns: questionID_userAns,
      courseId: courseId,
      sectionId: sectionId,
      contentId: activeContent.contentId, // CRITICAL: This ensures we submit the correct quiz
      userId: cookies.userId,
      status: "Completed",
      quizStartedAt: quizStartedAt,
      quizSubmittedAt: dayjs().toISOString(),
      ...(proctoringLogs && { proctoringLogs }),
      ...(activeContent?.isProctored && questionTimeTracking && Object.keys(questionTimeTracking).length > 0 && {
        questionTimeTracking: Object.entries(questionTimeTracking).map(([questionId, seconds]) => ({
          questionId,
          timeSpentSeconds: Math.round(seconds),
        })),
      }),
      ...(isMaxAttemptsQuiz && { isMaxAttemptsQuiz: true, attemptNumber }),
    };

    try {
      const response = await evaluateAnswer(payload);
    
      setReviewCorrect(response?.data?.results || []);
      
      const responseScore = response?.data?.score ?? 0;
      setUserScore(Number(responseScore.toFixed(2)));
      setScore(Number(responseScore.toFixed(2)));
      
      setPercentageScore(response?.data?.percentage_score ?? 0);
      setCorrectQuestionsCount(response?.data?.correctQuestions ?? 0);
      setTotalQuestionsCount(response?.data?.totalQuestions ?? 0);
      
      const trueMaxMarks = response?.data?.total_possible_marks || response?.data?.results?.reduce((sum, item) => sum + (Number(item.total_marks) || 1), 0);
      setTotalScore(trueMaxMarks || response?.data?.totalQuestions || Object.keys(selectedAnswers).length);
      setStatus(response?.data?.status ?? "submitted");
      if (!autoSubmitCountedRef.current) {
        onAttemptSubmitted?.(response?.data);
      }
    } catch (error) {
      console.error("Error submitting quiz:", error);
    } finally {
      setLoading(false);
    }
  };

  // Guard against refresh/close/network-loss during an active attempt: the student
  // shouldn't be able to dodge the attempt count by refreshing or going offline, so
  // whatever is currently selected gets auto-submitted and counted as an attempt.
  useEffect(() => {
    if (quizStatus !== "start") return;

    const handleBeforeUnload = (e) => {
      try {
        const questionID_userAns = (activeContent?.questions || []).map((question) => {
          const answer = selectedAnswers[question.id] || "";
          return {
            questionId: String(question.id),
            userAnswer: String(answer),
            answerSelectionType: question?.answerSelectionType || "single",
            point: question?.point || 1,
          };
        });
        const beaconPayload = {
          questionID_userAns,
          courseId,
          sectionId,
          contentId: activeContent?.contentId,
          userId: cookies.userId,
          status: "AutoSubmitted-Refresh",
          quizStartedAt,
          quizSubmittedAt: dayjs().toISOString(),
          ...(isMaxAttemptsQuiz && { isMaxAttemptsQuiz: true, attemptNumber }),
        };
        sendEvaluateAnswerBeacon(beaconPayload);
        autoSubmitCountedRef.current = true;
        onAttemptSubmitted?.();
      } catch (err) {
        console.error("Auto-submit on refresh/close failed:", err);
      }
      e.preventDefault();
      e.returnValue = "Refreshing or leaving this page now will auto-submit your quiz and use one attempt.";
      return e.returnValue;
    };

    const handleOffline = () => {
      showToast?.("Network connection lost — auto-submitting your quiz.", "warning");
      autoSubmitCountedRef.current = true;
      onAttemptSubmitted?.();
      setQuizStatus("timeout");
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("offline", handleOffline);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("offline", handleOffline);
    };
  }, [quizStatus, activeContent, selectedAnswers, courseId, sectionId, cookies.userId, quizStartedAt, isMaxAttemptsQuiz, attemptNumber, onAttemptSubmitted, setQuizStatus, showToast]);

  // Guard against in-app navigation away (sidebar link, switching to a different content item,
  // browser back through React Router, etc.) — none of these fire `beforeunload`, since the page
  // itself never unloads, only this component unmounts. Max-Attempts quizzes only: silently
  // auto-submit whatever's selected so the student can't dodge the attempt count by navigating
  // away mid-quiz instead of refreshing. Refs (not state) because a `useEffect` cleanup with an
  // empty dependency array only ever sees the values from the first render otherwise.
  const latestStateRef = useRef({});
  useEffect(() => {
    latestStateRef.current = {
      quizStatus,
      activeContent,
      selectedAnswers,
      isMaxAttemptsQuiz,
      attemptNumber,
      quizStartedAt,
      userId: cookies.userId,
    };
  });

  useEffect(() => {
    return () => {
      const {
        quizStatus: latestQuizStatus,
        activeContent: latestActiveContent,
        selectedAnswers: latestSelectedAnswers,
        isMaxAttemptsQuiz: latestIsMaxAttemptsQuiz,
        attemptNumber: latestAttemptNumber,
        quizStartedAt: latestQuizStartedAt,
        userId: latestUserId,
      } = latestStateRef.current;

      if (!latestIsMaxAttemptsQuiz || latestQuizStatus !== "start") return;

      try {
        const questionID_userAns = (latestActiveContent?.questions || []).map((question) => {
          const answer = latestSelectedAnswers[question.id] || "";
          return {
            questionId: String(question.id),
            userAnswer: String(answer),
            answerSelectionType: question?.answerSelectionType || "single",
            point: question?.point || 1,
          };
        });
        const payload = {
          questionID_userAns,
          courseId,
          sectionId,
          contentId: latestActiveContent?.contentId,
          userId: latestUserId,
          status: "AutoSubmitted-Navigation",
          quizStartedAt: latestQuizStartedAt,
          quizSubmittedAt: dayjs().toISOString(),
          isMaxAttemptsQuiz: true,
          attemptNumber: latestAttemptNumber,
        };
        // Fire-and-forget: the component is already unmounting, nothing to update locally.
        // The page itself stays alive (this is in-app navigation, not a real unload), so a
        // normal async call is fine here — sendEvaluateAnswerBeacon is only needed when the
        // browser tab itself is about to close.
        evaluateAnswer(payload).catch((err) => {
          console.error("Auto-submit on navigation away failed:", err);
        });
      } catch (err) {
        console.error("Auto-submit on navigation away failed:", err);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getCompletedSection = (data) => {
    const isCompleted = data?.completed || false;
    const quizData = getLatestQuizResults(data);
    if (
      quizData &&
      ["score", 
        "correctQuestions", 
        "totalQuestions"].every((key) =>
        Object.keys(quizData).includes(key)
      )
    ) {
      setQuizStatus("finish"); 
      //   setScore(Number(quizData.score.toFixed(2)));
      setUserScore(quizData.correctQuestions);
      setTotalScore(quizData.totalQuestions);
      setStatus(isCompleted);
      
      setCorrectQuestionsCount(quizData.correctQuestions);
      setTotalQuestionsCount(quizData.totalQuestions);
      setPercentageScore(quizData.percentage_score ?? quizData.percentage ?? quizData.score ?? 0);

      const percentage = (
        (Number(userScore) / Number(totalScore)) * 
        100
      ).toFixed(2);
      // setScore(percentage); 

      const completedSelectedAnswers = quizData.results;
      setReviewCorrect(completedSelectedAnswers);

      const answers = completedSelectedAnswers.reduce((acc, item) => {
        acc[item.questionId] = parseInt(item.userAnswer, 10);
        return acc;
      }, {});
      setSelectedAnswers(answers);
    }

    const isReview = location?.state?.reviewQuiz;

    if (isCompleted || isReview) {
      setQuizStatus("finish");
    }
  };

  useEffect(() => {
    if (quizStatus === "start") {
      setScore("");
      setUserScore("");
      setTotalScore("");
      setStatus("");
      setSelectedAnswers({});
      setQuestionTimeTracking({});
      if (setQuizStartedAt) {
        setQuizStartedAt(dayjs().toISOString());
      }
    }
  }, [quizStatus, setQuizStartedAt]);

  useEffect(() => {
    // Only process if this is an MCQ quiz - prevent cross-contamination with subjective quizzes
    if (activeContent?.contentType !== 'quiz' && activeContent?.contentType !== 'quizSubjective') {
      return;
    }

    // console.log("activeContent", activeContent);
    if (activeContent?.questions) {
      // console.log(
      //   "Questions with imageName and imageOptionName:",
      //   activeContent.questions.map((q) => ({
      //     id: q.id,
      //     imageName: q.imageName,
      //     answers: q.answers.map((a) => ({
      //       value: a.value,
      //       imageOptionName: a.imageOptionName,
      //     })),
      //   }))
      // );
    }

    if (activeContent.completed) {
      getCompletedSection(activeContent);
      setPassPercentage(activeContent.passingPercentage);
      const latestResults = getLatestQuizResults(activeContent);
      setUserScore(latestResults?.correctQuestions);
      setScore(latestResults?.correctQuestions);
      // activeContent.questions can be [] on Max-Attempts records (question bank isn't
      // always included on this response shape) — fall back to the result set's own count.
      setTotalScore(activeContent.questions?.length || latestResults?.totalQuestions || 0);
      setStatus(activeContent.completed);
    }
  }, [activeContent]);

  // Production-style: Simple useEffect that only depends on quizStatus
  // React's component lifecycle ensures only the mounted component processes this
  useEffect(() => {
    if (quizStatus === "finish" || quizStatus === "timeout") {
      getFinish();
    }
  }, [quizStatus]);

  // CRITICAL: Early return guard AFTER all hooks - prevent rendering if not an MCQ quiz
  if (activeContent?.contentType !== 'quiz' && activeContent?.contentType !== 'quizSubjective') {
    return null;
  }

  const retryQuiz = () => {
    setQuizStatus("start");
  };

  const getSelectedAnswers = (data) => {
    setSelectedAnswers(data);
  };

  const handleProctoringLogs = (logs) => {
    setProctoringLogs(logs);
  };

  const handleQuestionTimeTracking = (data) => {
    setQuestionTimeTracking(data);
  };

  return (
    <>
      {loading ? (
        <>
          <section className="mt-4 course-container overflow-auto">
            <Container fluid>
              <div className="content">
                <div className="d-flex justify-content-center align-items-center w-100 h-100 course-container">
                  <Spinner />
                </div>
              </div>
            </Container>
          </section>
        </>
      ) : null}

      {!activeContent.completed && quizStatus !== "finish" && quizStatus !== "timeout" ? (
        <>
          <section className="mt-4 course-container overflow-auto">
            <Container fluid>
              <div className="content">
                {isLocked ? (
                  <div className="d-flex flex-column align-items-center justify-content-center h-100">
                    {/* Edge case: teacher reviewed, redo required, but window is closed */}
                    {activeContent?.teacherReviewed && activeContent?.redo && !lockReason.includes("available on") ? (
                      failedRedoReviewMode ? (
                        <div className="w-100">
                          <div className="d-flex justify-content-start mb-3">
                            <Button variant="secondary" size="sm" onClick={() => setFailedRedoReviewMode(false)}>
                              Back
                            </Button>
                          </div>
                          <Card className="mb-4">
                            <Card.Body className="card-body-scroll">
                              <h4 className="mb-3">Review Questions</h4>
                              <div className="mt-4">
                                <Questions
                                  questions={activeContent.questions}
                                  selectedAnswers={getLatestQuizResults(activeContent)?.results?.reduce((acc, item) => {
                                    acc[item.questionId] = item.userAnswer;
                                    return acc;
                                  }, {}) || {}}
                                  isReview={true}
                                  correctAnswers={getLatestQuizResults(activeContent)?.results || []}
                                  result={getLatestQuizResults(activeContent)?.results || []}
                                />
                              </div>
                            </Card.Body>
                          </Card>
                        </div>
                      ) : (
                        <>
                          <div className="text-center p-4">
                            <AlertTriangle size={64} className="text-danger mb-3" />
                            <h3 className="text-danger fw-bold mb-2">Quiz Failed</h3>
                            <p className="text-muted fs-5 mb-3">
                              You did not meet the passing criteria after the course manager's review.
                            </p>
                            <Alert variant="warning" className="mb-4 d-inline-block">
                              <strong>Quiz window has closed.</strong> You can no longer retake this quiz.
                            </Alert>
                          </div>
                          <Button
                            variant="outline-primary"
                            className="px-4 py-2 fw-bold shadow-sm"
                            onClick={() => setFailedRedoReviewMode(true)}
                          >
                            Review Answers
                          </Button>
                        </>
                      )
                    ) : (
                      <ReadyForQuiz
                        title={lockReason.includes("available on") ? "Quiz Scheduled" : "Quiz Closed"}
                        description={lockReason}
                        buttonText={lockReason.includes("available on") ? "Coming Soon" : "Window Closed"}
                        isDisabled={true}
                        showStats={false}
                      />
                    )}
                    <div className="mt-3">
                      <NavigationButton
                        courseDetails={courseDetails}
                        activeContent={activeContent}
                        setActiveContent={setActiveContent}
                      />
                    </div>
                  </div>
                ) : (
                  <QuizStart
                    modifyGlobalQuizStatus={setQuizStatus}
                    quizStatus={quizStatus}
                    getSelectedAnswers={getSelectedAnswers}
                    getProctoringLogs={handleProctoringLogs}
                    getQuestionTimeTracking={handleQuestionTimeTracking}
                    quiz={activeContent}
                    quizTime={quizTime}
                    maxAttempts={maxAttemptsLimit}
                    remainingAttempts={remainingAttempts}
                  />
                )}
              </div>
            </Container>
          </section>

          {/* Late Start Warning Modal */}
          <Modal centered show={showLateModal} onHide={() => setShowLateModal(false)} backdrop="static" keyboard={false}>
            <Modal.Header closeButton variant="white" className="bg-warning text-dark border-0">
              <Modal.Title className="fw-bold">
                <Info size={24} className="me-2" />
                Late Start Warning
              </Modal.Title>
            </Modal.Header>
            <Modal.Body className="py-4">
              <p className="fs-5 mb-3">
                You are starting this quiz close to the scheduled end time. 
              </p>
              <Alert variant="danger" className="mb-0">
                <strong>Important:</strong> Your time has been reduced to <strong>{quizTime} minutes</strong> to align with the quiz window closing at <strong>{activeContent.endTime}</strong>.
              </Alert>
            </Modal.Body>
            <Modal.Footer className="border-0">
              <Button variant="primary" onClick={() => setShowLateModal(false)} className="px-4 py-2 fw-bold">
                I Understand, Let's Start
              </Button>
            </Modal.Footer>
          </Modal>
        </>
      ) : null}

      {activeContent.completed || quizStatus === "finish" ? (
        <>
          <section className="mt-4 course-container overflow-auto">
            <Container fluid>
              <div className="content">
                {score !== undefined && (
                  <QuizResult
                    loading={loading}
                    quizStatus={quizStatus}
                    quiz={activeContent}
                    selectedAnswers={selectedAnswers}
                    correctAnswers={reviewCorrect}
                    score={score}
                    passPercentage={passPercentage}
                    userScore={userScore}
                    totalScore={totalScore}
                    percentageScore={percentageScore}
                    correctQuestionsCount={correctQuestionsCount}
                    totalQuestionsCount={totalQuestionsCount}
                    status={status}
                    retryQuiz={retryQuiz}
                  />
                )}
              </div>
            </Container>
          </section>
          <NavigationButton
            courseDetails={courseDetails}
            activeContent={activeContent}
            setActiveContent={setActiveContent}
          />
        </>
      ) : null}

      {quizStatus === "timeout" ? (
        <>
          <section className="mt-4 course-container overflow-auto">
            <Container fluid>
              <div className="content">
                {score !== undefined && (
                  <QuizResult
                    loading={loading}
                    quizStatus={quizStatus}
                    quiz={activeContent}
                    selectedAnswers={selectedAnswers}
                    correctAnswers={reviewCorrect}
                    score={score}
                    passPercentage={passPercentage}
                    userScore={userScore}
                    totalScore={totalScore}
                    percentageScore={percentageScore}
                    correctQuestionsCount={correctQuestionsCount}
                    totalQuestionsCount={totalQuestionsCount}
                    status={status}
                    retryQuiz={retryQuiz}
                  />
                )}
              </div>
            </Container>
          </section>
          <NavigationButton
            courseDetails={courseDetails}
            activeContent={activeContent}
            setActiveContent={setActiveContent}
          />
        </>
      ) : null}
    </>
  );
};

export default QuizViewer;